import {
  APIReferenceMember,
  ReferenceType,
  memberHasParameters,
  memberHasReturnValues,
  memberHasType,
} from '@modules/client';
import React, { FC, useMemo } from 'react';
import isTypeOptional from '@modules/utilities/pageBuild/isTypeOptional';
import DynamicallyRouted from '@modules/miscellaneous/common/components/DynamicallyRouted';
import useResponsiveDesign from '@modules/miscellaneous/common/hooks/useResponsiveDesign';
import useReferenceSummaryMemberTableStyles from './ReferenceSummaryMemberTable.styles';
import useReferenceSummaryMemberTableStylesV2 from './ReferenceSummaryMemberTableV2.styles';
import { getMemberSlug as getMemberDetailHashIdFromName } from '../../utils/apiReferenceUtils';
import { useReferenceTypesData } from './ReferenceTypesDataProvider';
import ReferenceMemberType from './ReferenceMemberType';
import ReferenceMemberParameters from './ReferenceMemberParameters';
import ReferenceMemberParametersV2 from './ReferenceMemberParametersV2';
import ReferenceMemberReturns from './ReferenceMemberReturns';
import ReferenceMemberTitle from './ReferenceMemberTitle';
import SpaceSuffixedColon from './SpaceSuffixedColon';
import {
  getReferencePageUrl,
  getUniqueMemberTitleHashId,
} from '../../utils/apiReferencePageNavUtils';

interface ReferenceSummaryMemberTrTitleProps {
  member: APIReferenceMember;
  referenceType: ReferenceType;
  referenceName: string; // 'Bone', 'CFrame' etc
  isInheritedMember?: boolean;
  isEventMemberType: boolean;
  isSameNameAsPrevMember: boolean;
  engineRevampEnabled?: boolean;
}

const ReferenceSummaryMemberTrTitle: FC<
  React.PropsWithChildren<ReferenceSummaryMemberTrTitleProps>
> = ({
  member,
  referenceType,
  referenceName,
  isInheritedMember,
  isEventMemberType,
  isSameNameAsPrevMember,
  engineRevampEnabled = false,
}) => {
  const { getTypeAliasDatum, eventReturnTypeAliasDatum } = useReferenceTypesData();
  const { classes: defaultClasses } = useReferenceSummaryMemberTableStyles();
  const { classes: v2Classes } = useReferenceSummaryMemberTableStylesV2();
  const classes = engineRevampEnabled ? v2Classes : defaultClasses;
  const { isMobile } = useResponsiveDesign();
  // Hide signatures on mobile only when engine revamp is enabled
  const hideSignature = engineRevampEnabled && isMobile;
  const titleHrefId = useMemo(() => {
    return getUniqueMemberTitleHashId(member, isSameNameAsPrevMember);
  }, [member, isSameNameAsPrevMember]);
  const { typeDatum, typeIsOptional } = useMemo(() => {
    if (memberHasType(member)) {
      const typeDatumVal = getTypeAliasDatum(member.type);
      if (!typeDatumVal)
        throw new Error(`No type data found for ${member.type} but member has type`);
      return {
        typeDatum: typeDatumVal,
        typeIsOptional: isTypeOptional(member.type),
      };
    }
    return {};
  }, [member, getTypeAliasDatum]);
  const memberDetailSectionHash = useMemo(
    () => `#${getMemberDetailHashIdFromName(titleHrefId)}`,
    [titleHrefId],
  );
  const hasParameters = memberHasParameters(member);

  return (
    <React.Fragment>
      <DynamicallyRouted.Link
        variant='body1'
        href={
          isInheritedMember
            ? getReferencePageUrl(referenceType, referenceName, memberDetailSectionHash)
            : // navigate to section on same member page
              memberDetailSectionHash
        }
        className={classes.titleLink}>
        <ReferenceMemberTitle member={member} />
      </DynamicallyRouted.Link>
      {/* Show empty () for callable members (methods/events/constructors) when signature is hidden */}
      {hideSignature && hasParameters && <span>()</span>}
      {/* Property type signature (hidden on mobile) */}
      {!hideSignature && typeDatum && (
        <React.Fragment>
          <SpaceSuffixedColon engineRevampEnabled={engineRevampEnabled} />
          <ReferenceMemberType
            parameter={typeDatum}
            isOptional={!!typeIsOptional}
            engineRevampEnabled={engineRevampEnabled}
          />
        </React.Fragment>
      )}
      {/* Method/function parameters (hidden on mobile) */}
      {!hideSignature && hasParameters && (
        <React.Fragment>
          {engineRevampEnabled ? (
            <ReferenceMemberParametersV2 parameters={member.parameters} />
          ) : (
            <ReferenceMemberParameters parameters={member.parameters} />
          )}
        </React.Fragment>
      )}
      {/* Event type signature (hidden on mobile) */}
      {!hideSignature && isEventMemberType && (
        <span style={{ whiteSpace: 'nowrap' }}>
          <SpaceSuffixedColon engineRevampEnabled={engineRevampEnabled} />
          <ReferenceMemberType
            parameter={eventReturnTypeAliasDatum}
            isOptional={false}
            engineRevampEnabled={engineRevampEnabled}
          />
        </span>
      )}
      {!hideSignature &&
        memberHasReturnValues(member) &&
        !!member.returns &&
        member.returns.length > 0 && (
          <span style={{ whiteSpace: 'nowrap' }}>
            <SpaceSuffixedColon engineRevampEnabled={engineRevampEnabled} />
            <ReferenceMemberReturns
              returns={member.returns}
              engineRevampEnabled={engineRevampEnabled}
            />
          </span>
        )}
    </React.Fragment>
  );
};

export default ReferenceSummaryMemberTrTitle;
